/*
 * PRD-037 — Editor de horarios del wizard "Preparar comercio" (app SEPARADA del partner).
 *
 * Reemplaza SOLO en el wizard partner al `ScheduleManager` del panel (que reusaba `Input.Hours` de
 * @plick/ui: un `<input type=time>` con un ícono de reloj absoluto que reserva ~36px de padding
 * izquierdo; en el card angosto del wizard a 320px el valor de la hora quedaba tapado y "solo se veía
 * el ícono"). Aquí el picker es un `<input type=time>` nativo SIN ícono superpuesto: el valor está
 * SIEMPRE visible y el sistema operativo aporta un selector de hora táctil grande y familiar.
 *
 * Diseño V4 inline puro (cero Tailwind, cero @plick/ui, sin ThemeProvider) — mismo contrato de datos
 * que `ScheduleManager` (`ScheduleItem[]` plano + `onChange`/`autoSave`), así el backend y el SETUP
 * del dueño reciben el formato idéntico. NO se toca el ScheduleManager del panel.
 *
 * UX mobile-first:
 *  - Una card por día. Encabezado: nombre del día + toggle claro Abierto/Cerrado (role=switch, con
 *    texto, no un ícono ambiguo).
 *  - Por día, uno o más rangos "Abre/Cierra" con pickers nativos legibles a 320px (font 16 → sin
 *    zoom en iOS), etiquetados.
 *  - Acciones con texto explícito: "Agregar horario" y "Copiar a otros días" (panel inline con chips
 *    de día seleccionables — sin overlay/z-index, touch-friendly). "Quitar" por rango adicional.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { weekDays } from '@utils/constants';
import { V4 } from '@components/v4/tokens';
import type { ScheduleItem, ScheduleManagerProps } from '@components/schedule/ScheduleManager.types';

const DEFAULT_OPEN = '08:15';
const DEFAULT_CLOSE = '18:30';
const WEEKDAY_VALUES = ['1', '2', '3', '4', '5']; // Lunes–Viernes (para el atajo del copiado)

// ── helpers puros sobre el array plano ──────────────────────────────────────────────────────────
// El modelo es un `ScheduleItem[]` plano (varios turnos por día se repiten con el mismo `dia_semana`).
// Localizamos el turno físico por (día, índice-dentro-del-día) para editar/borrar sin ambigüedad.
const findFlatIndex = (list: ScheduleItem[], day: string, dayIdx: number): number => {
  let seen = -1;
  for (let i = 0; i < list.length; i += 1) {
    if (list[i].dia_semana === day) {
      seen += 1;
      if (seen === dayIdx) return i;
    }
  }
  return -1;
};

const turnsForDay = (list: ScheduleItem[], day: string): ScheduleItem[] =>
  list.filter((item) => item.dia_semana === day);

// Semilla por defecto — idéntica a la del ScheduleManager del panel para no cambiar el dato que se
// persiste (todos los días 08:15–18:30; Sábado cierra 13:30).
const buildDefaultSchedules = (): ScheduleItem[] =>
  ['0', '1', '2', '3', '4', '5', '6'].map((dia) => ({
    dia_semana: dia,
    horario_apertura: DEFAULT_OPEN,
    horario_cierre: dia === '6' ? '13:30' : DEFAULT_CLOSE,
  }));

// ── iconografía inline (V4: SVG, no dependencia de @tabler) ──────────────────────────────────────
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);
const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2.2" stroke="currentColor" strokeWidth="2" />
    <path d="M5 15V6a2 2 0 0 1 2-2h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 7h14M10 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Toggle Abierto/Cerrado ───────────────────────────────────────────────────────────────────────
// Switch accesible (role=switch + aria-checked) con etiqueta de texto: en touch es inequívoco (no un
// ícono ambiguo). Área táctil ≥44px de alto.
const DayToggle = ({
  on,
  disabled,
  onToggle,
  ariaLabel,
}: {
  on: boolean;
  disabled?: boolean;
  onToggle: (next: boolean) => void;
  ariaLabel: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onToggle(!on)}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      minHeight: 44,
      padding: '4px 6px 4px 8px',
      background: 'transparent',
      border: 0,
      cursor: disabled ? 'default' : 'pointer',
      fontFamily: 'inherit',
      opacity: disabled ? 0.5 : 1,
      flexShrink: 0,
    }}
  >
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: on ? V4.secondaryDark : V4.grayUltraDark,
      }}
    >
      {on ? 'Abierto' : 'Cerrado'}
    </span>
    <span
      aria-hidden="true"
      style={{
        position: 'relative',
        width: 46,
        height: 26,
        borderRadius: 9999,
        background: on ? V4.secondary : V4.gray,
        transition: 'background 160ms cubic-bezier(.4,0,.2,1)',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: on ? 23 : 3,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: V4.white,
          boxShadow: '0 1px 3px rgba(0,0,0,.25)',
          transition: 'left 160ms cubic-bezier(.4,0,.2,1)',
        }}
      />
    </span>
  </button>
);

// ── Campo de hora nativo ─────────────────────────────────────────────────────────────────────────
// `<input type=time>` sin ícono superpuesto: el valor está SIEMPRE visible. font 16 evita el zoom de
// iOS al enfocar. Borde secundario al foco (patrón V4).
const TimeField = ({
  label,
  value,
  disabled,
  onChange,
  name,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  name: string;
}) => {
  const [focused, setFocused] = useState(false);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 96px', minWidth: 0 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: V4.grayUltraDark,
        }}
      >
        {label}
      </span>
      <input
        type="time"
        name={name}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          border: `1.5px solid ${focused && !disabled ? V4.secondary : V4.grayLight}`,
          borderRadius: 8,
          background: disabled ? V4.grayUltraLight : V4.white,
          padding: '10px 12px',
          fontSize: 16,
          fontWeight: 600,
          fontFamily: 'inherit',
          color: V4.primary,
          outline: 0,
          transition: 'border-color 150ms',
        }}
      />
    </label>
  );
};

// ── Botón de texto secundario (acciones de día) ──────────────────────────────────────────────────
const TextAction = ({
  onClick,
  icon,
  children,
  tone = 'neutral',
  disabled,
}: {
  onClick: () => void;
  icon: ReactNode;
  children: ReactNode;
  tone?: 'neutral' | 'danger';
  disabled?: boolean;
}) => {
  const [hover, setHover] = useState(false);
  const color = tone === 'danger' ? V4.danger : V4.secondaryDark;
  const hoverBg = tone === 'danger' ? V4.dangerFaded : V4.grayUltraLight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: 40,
        padding: '8px 12px',
        borderRadius: 8,
        border: 0,
        background: hover && !disabled ? hoverBg : 'transparent',
        color,
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 150ms',
      }}
    >
      {icon}
      {children}
    </button>
  );
};

// Atajo de selección rápida dentro del panel de copiado (Lun–Vie / Todos / Ninguno).
const QuickChip = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      minHeight: 34,
      padding: '6px 12px',
      borderRadius: 8,
      border: `1px solid ${V4.grayLight}`,
      background: V4.white,
      color: V4.secondaryDark,
      fontFamily: 'inherit',
      fontSize: 12,
      fontWeight: 700,
      cursor: 'pointer',
    }}
  >
    {label}
  </button>
);

// Botón de acción del panel de copiado (confirmar / cancelar).
const PanelButton = ({
  variant,
  disabled,
  onClick,
  children,
}: {
  variant: 'primary' | 'ghost';
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => {
  const isPrimary = variant === 'primary';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        minHeight: 40,
        padding: '0 16px',
        borderRadius: 6,
        border: `1.5px solid ${isPrimary ? V4.secondary : V4.gray}`,
        background: isPrimary ? V4.secondary : V4.white,
        color: isPrimary ? V4.white : V4.primary,
        fontFamily: 'inherit',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
};

// ── Panel inline "Copiar a otros días" ───────────────────────────────────────────────────────────
// Disclosure inline (sin overlay ni z-index → no requiere OverlayGate y es más simple en touch).
// Chips de día seleccionables (aria-pressed) + atajos + confirmar.
const CopyPanel = ({
  sourceDay,
  onCopy,
  onClose,
}: {
  sourceDay: string;
  onCopy: (targetDays: string[]) => void;
  onClose: () => void;
}) => {
  const others = weekDays.filter((d) => d.value !== sourceDay);
  const [selected, setSelected] = useState<string[]>([]);
  const sourceLabel = weekDays.find((d) => d.value === sourceDay)?.label ?? '';

  const toggle = (value: string) =>
    setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  return (
    <div
      style={{
        marginTop: 12,
        padding: 14,
        borderRadius: 10,
        background: V4.grayUltraLight,
        border: `1px solid ${V4.grayLight}`,
      }}
    >
      <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: V4.primary }}>
        Copiar el horario de {sourceLabel} a:
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {others.map((d) => {
          const active = selected.includes(d.value);
          return (
            <button
              key={d.value}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(d.value)}
              style={{
                minHeight: 40,
                padding: '8px 14px',
                borderRadius: 9999,
                border: `1.5px solid ${active ? V4.secondary : V4.grayLight}`,
                background: active ? V4.secondary : V4.white,
                color: active ? V4.white : V4.primary,
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 150ms, border-color 150ms, color 150ms',
              }}
            >
              {d.label}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
        <QuickChip label="Lun–Vie" onClick={() => setSelected(WEEKDAY_VALUES.filter((v) => v !== sourceDay))} />
        <QuickChip label="Todos" onClick={() => setSelected(others.map((d) => d.value))} />
        <QuickChip label="Ninguno" onClick={() => setSelected([])} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
        <PanelButton variant="ghost" onClick={onClose}>
          Cancelar
        </PanelButton>
        <PanelButton variant="primary" disabled={selected.length === 0} onClick={() => onCopy(selected)}>
          {selected.length > 0 ? `Copiar a ${selected.length} ${selected.length === 1 ? 'día' : 'días'}` : 'Copiar'}
        </PanelButton>
      </div>
    </div>
  );
};

// ── Card de un día ────────────────────────────────────────────────────────────────────────────────
const DayCard = ({
  day,
  label,
  turns,
  readOnly,
  onToggleDay,
  onAddTurn,
  onRemoveTurn,
  onChangeTurn,
  onCopy,
}: {
  day: string;
  label: string;
  turns: ScheduleItem[];
  readOnly?: boolean;
  onToggleDay: (dayValue: string, on: boolean) => void;
  onAddTurn: (dayValue: string) => void;
  onRemoveTurn: (dayValue: string, dayIdx: number) => void;
  onChangeTurn: (dayValue: string, dayIdx: number, patch: Partial<ScheduleItem>) => void;
  onCopy: (dayValue: string, targetDays: string[]) => void;
}) => {
  const [copyOpen, setCopyOpen] = useState(false);
  const isOpen = turns.length > 0;

  return (
    <div
      style={{
        border: `1px solid ${V4.grayLight}`,
        borderRadius: 12,
        background: isOpen ? V4.white : V4.grayBlueLight,
        padding: 14,
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: V4.primary }}>{label}</span>
        <DayToggle
          on={isOpen}
          disabled={readOnly}
          ariaLabel={`${label}: ${isOpen ? 'abierto' : 'cerrado'}`}
          onToggle={(next) => {
            setCopyOpen(false);
            onToggleDay(day, next);
          }}
        />
      </div>

      {isOpen && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {turns.map((turn, idx) => (
            <div key={`${day}_${idx}`} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1, minWidth: 0 }}>
                <TimeField
                  label="Abre"
                  name={`open_${day}_${idx}`}
                  value={turn.horario_apertura}
                  disabled={readOnly}
                  onChange={(v) => onChangeTurn(day, idx, { horario_apertura: v })}
                />
                <TimeField
                  label="Cierra"
                  name={`close_${day}_${idx}`}
                  value={turn.horario_cierre}
                  disabled={readOnly}
                  onChange={(v) => onChangeTurn(day, idx, { horario_cierre: v })}
                />
              </div>
              {!readOnly && idx > 0 && (
                <button
                  type="button"
                  aria-label={`Quitar horario ${idx + 1} de ${label}`}
                  onClick={() => onRemoveTurn(day, idx)}
                  style={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 8,
                    border: `1px solid ${V4.grayLight}`,
                    background: V4.white,
                    color: V4.danger,
                    cursor: 'pointer',
                  }}
                >
                  <TrashIcon />
                </button>
              )}
            </div>
          ))}

          {!readOnly && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
              <TextAction icon={<PlusIcon />} onClick={() => onAddTurn(day)}>
                Agregar horario
              </TextAction>
              <TextAction icon={<CopyIcon />} onClick={() => setCopyOpen((v) => !v)}>
                Copiar a otros días
              </TextAction>
            </div>
          )}

          {copyOpen && !readOnly && (
            <CopyPanel
              sourceDay={day}
              onClose={() => setCopyOpen(false)}
              onCopy={(targetDays) => {
                onCopy(day, targetDays);
                setCopyOpen(false);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ── Componente principal ─────────────────────────────────────────────────────────────────────────
const PartnerScheduleEditor = ({
  schedules,
  onChange,
  autoSave = false,
  readOnly = false,
  customTitle = 'Horarios de atención',
}: ScheduleManagerProps) => {
  const [localSchedules, setLocalSchedules] = useState<ScheduleItem[]>(schedules || []);

  useEffect(() => {
    setLocalSchedules(schedules || []);
  }, [schedules]);

  const applyChanges = (next: ScheduleItem[]) => {
    setLocalSchedules(next);
    if (autoSave) onChange(next);
  };

  // Semilla por defecto al montar si el borrador aún no tiene horarios (paridad con el panel).
  // Deps vacías a propósito: solo debe sembrar una vez, en el montaje.
  useEffect(() => {
    if (!localSchedules || localSchedules.length === 0) {
      applyChanges(buildDefaultSchedules());
    }
  }, []);

  const setDayEnabled = (day: string, on: boolean) => {
    if (on) {
      applyChanges([
        ...localSchedules,
        { dia_semana: day, horario_apertura: DEFAULT_OPEN, horario_cierre: DEFAULT_CLOSE },
      ]);
    } else {
      applyChanges(localSchedules.filter((item) => item.dia_semana !== day));
    }
  };

  const addTurn = (day: string) => {
    applyChanges([
      ...localSchedules,
      { dia_semana: day, horario_apertura: DEFAULT_OPEN, horario_cierre: DEFAULT_CLOSE },
    ]);
  };

  const removeTurn = (day: string, dayIdx: number) => {
    const flat = findFlatIndex(localSchedules, day, dayIdx);
    if (flat < 0) return;
    applyChanges(localSchedules.filter((_, i) => i !== flat));
  };

  const changeTurn = (day: string, dayIdx: number, patch: Partial<ScheduleItem>) => {
    const flat = findFlatIndex(localSchedules, day, dayIdx);
    if (flat < 0) return;
    applyChanges(localSchedules.map((item, i) => (i === flat ? { ...item, ...patch } : item)));
  };

  const copyToDays = (sourceDay: string, targetDays: string[]) => {
    const sourceTurns = turnsForDay(localSchedules, sourceDay);
    const withoutTargets = localSchedules.filter((item) => !targetDays.includes(item.dia_semana));
    const added: ScheduleItem[] = [];
    targetDays.forEach((target) => {
      sourceTurns.forEach((turn) => added.push({ ...turn, dia_semana: target }));
    });
    applyChanges([...withoutTargets, ...added]);
  };

  if (!localSchedules) return null;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: V4.primary }}>{customTitle}</h3>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: V4.grayUltraDark, lineHeight: 1.4 }}>
          Activa los días que abres y define uno o más horarios por día. Puedes copiar el horario de un día a los demás.
        </p>
      </div>

      {weekDays.map((day) => (
        <DayCard
          key={day.value}
          day={day.value}
          label={day.label}
          turns={turnsForDay(localSchedules, day.value)}
          readOnly={readOnly}
          onToggleDay={setDayEnabled}
          onAddTurn={addTurn}
          onRemoveTurn={removeTurn}
          onChangeTurn={changeTurn}
          onCopy={copyToDays}
        />
      ))}
    </div>
  );
};

export default PartnerScheduleEditor;
